/**
 * React wrapper around `StageComponent` for rendering a screen inside the
 * viewer host container.
 *
 * It applies container and theme CSS, wires annotation callbacks, observes
 * page-number changes, restores spotlight state after re-renders, scrolls to
 * the requested item when a new screen mounts
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useLayoutEffect,
  useMemo,
} from "react";
import { StageComponent } from "../../renderers/spf-renderers/stage";
import { injectThemeCSS, injectContainerStyles } from "../../util/cssProcessor";
import { applyThemeVariantBodyClass } from "../../util/bronteThemeClass";
import {
  applySpotlight as applySpotlightUtil,
  applySpotlights as applySpotlightsUtil,
  clearSpotlight as clearSpotlightUtil,
  clearSpotlights as clearSpotlightsUtil,
  isValidSpotlightConfig,
} from "../../util/spotlight";
import { scrollToElementByDataId } from "../../util/scrollToElement";
import { resetScrollRoot } from "../../util/getScrollRoot";
import { printContainer } from "../../util/print";
import { DEVICE_TYPE, type DeviceType } from "../../constants";
import {
  initializeAnnotationCallbacks,
  setAnnotationCallbacks,
  annotationScreen,
  addAnnotation as addAnnotationModule,
} from "../../callbacks/annotations/annotations";
import type {
  GetAuthTokenCallback,
  ScreenConfig,
  Spotlight,
  SpotlightApplyOptions,
  ThemeVariant,
  ViewerDeviceRuntime,
} from "../../types";
import type { Link, ScrollData } from "@bronte/viewer/src/data/ViewerConfig";
import type { IAnnotation, Media, RendererTypes } from "@bronte/viewer/src/main";
import { attachDelegatedExternalLinkClick } from "../../util/containerExternalLinkDelegation";
import { VIEWER_EXPRESS_CONTAINER_ID, DEFAULT_THEME_VARIANT } from "../constants";
import { usePageNumberObserver } from "../hooks/usePageNumberObserver";
import { usePageScrollNotifier } from "../hooks/usePageScrollNotifier";
import { initViewerMath, renderViewerMathInContainer } from "../../util/viewerMath";
import type { CSSProperties } from "react";

export type ViewerExpressReactComponentHandle = {
  applySpotlight: (options: SpotlightApplyOptions) => void;
  clearSpotlight: () => void;
  addAnnotation: (annotation: IAnnotation) => void;
  destroyPageObserver: () => void;
  notifyPageByDataId: (dataId: string) => void;
  /** Prints the current screen only (not the surrounding host page). */
  print: () => void;
};

export interface ViewerExpressPageData {
  screenConfig: ScreenConfig;
  elementId?: string;
  dataId?: string;
}

export type ViewerExpressReactComponentProps = {
  onClickReferenceLink: (dataId: string, referenceId: string) => void;
  onRequestAnnotations?: (annotation: IAnnotation) => void;
  onAnnotationIconClick?: (data: any) => void;
  onRequestGlossaryData: () => Promise<any[]>;
  onRequestAuthToken?: GetAuthTokenCallback;
  onComponentReRendered?: (elementId?: string) => void;
  onMediaClick?: (data: Media) => void;
  onMediaPlay?: (data: Media) => void;
  onLinkClick?: (data: Link) => void;
  /** Required with `renderer: MOBILE` for delegated external links (iOS/Android shell). */
  deviceType?: DeviceType;
  /** Host-supplied online check from `ViewerConfig.isOnline`. */
  isOnline?: () => boolean;
  /** Offline package root from `ViewerConfig.packagePath`. */
  packagePath?: string;
  /** With `deviceType` ios/android, `MOBILE` enables external-link delegation. */
  renderer?: RendererTypes;
  onPageChange?: (data: { pageNumber: string }) => void;
  onPageScroll?: (data: ScrollData) => void;
  onRequestScreenPageNumbers?: (
    elementId: string,
  ) => Array<{ pageNumber: string; dataId?: string }>;
  env?: string;
  page: ViewerExpressPageData | null;
  themeVariant?: Partial<ThemeVariant>;
};

export const ViewerExpressReactComponent = forwardRef<
  ViewerExpressReactComponentHandle,
  ViewerExpressReactComponentProps
>(function ViewerExpressReactComponent(
  {
    onClickReferenceLink,
    onRequestAnnotations,
    onAnnotationIconClick,
    onRequestGlossaryData,
    onRequestAuthToken,
    onComponentReRendered,
    onPageChange,
    onPageScroll,
    onRequestScreenPageNumbers,
    onMediaClick,
    onMediaPlay,
    onLinkClick,
    deviceType,
    isOnline,
    packagePath,
    renderer,
    env,
    page,
    themeVariant,
  },
  ref,
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onLinkClickRef = useRef(onLinkClick);
  const deviceTypeRef = useRef(deviceType);
  const rendererRef = useRef(renderer);
  onLinkClickRef.current = onLinkClick;
  deviceTypeRef.current = deviceType;
  rendererRef.current = renderer;
  const hostStyle: CSSProperties = {
    height: "100%",
    position: "relative",
    overflow: "hidden",
    minWidth: 0,
  };
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const spotlightStateRef = useRef<{
    spotlight: Spotlight | null;
    lastSelector: string | null;
  }>({ spotlight: null, lastSelector: null });
  const batchSpotlightsRef = useRef<Spotlight[]>([]);
  const pendingTodoRef = useRef<{ dataId?: string; elementId?: string } | null>(null);
  const prevScreenKeyRef = useRef<string | undefined>(undefined);

  const activePage = page;
  const activeThemeVariant = themeVariant ?? DEFAULT_THEME_VARIANT;
  const viewerDevice = useMemo<ViewerDeviceRuntime>(
    () => ({
      deviceType: deviceType ?? DEVICE_TYPE.WEB,
      isOnline: () => isOnline?.() ?? true,
      packagePath,
    }),
    [deviceType, isOnline, packagePath],
  );
  const currentElementIdRef = useRef<string>("");
  currentElementIdRef.current = activePage?.elementId ?? "";

  const {
    setupObserver: setupPageObserver,
    destroyObserver: destroyPageObserver,
    notifyPageByDataId,
  } = usePageNumberObserver(
    containerEl,
    { onPageChange, onRequestScreenPageNumbers },
    currentElementIdRef.current,
  );

  usePageScrollNotifier(containerEl, onPageScroll, activePage?.elementId ?? "");

  useEffect(() => {
    console.log("ViewerExpressReactComponent: Didmounted");
    injectContainerStyles();
    initViewerMath(packagePath);
  }, [packagePath]);

  // Must run before paint: useEffect deferred theme injection causes a flash of unthemed
  useLayoutEffect(() => {
    const theme = activeThemeVariant ?? DEFAULT_THEME_VARIANT;
    applyThemeVariantBodyClass(theme);
    injectThemeCSS(theme);
  }, [activeThemeVariant]);

  useEffect(() => {
    const root = hostRef.current;
    if (!root) return;
    initializeAnnotationCallbacks(deviceType ?? DEVICE_TYPE.WEB, root, {
      AnnotationRequestCallback: onRequestAnnotations,
    });
  }, [onRequestAnnotations, deviceType]);

  useEffect(() => {
    setAnnotationCallbacks({
      AnnotationRequestCallback: onRequestAnnotations,
      AnnotationIconClickCallback: onAnnotationIconClick,
    });
  }, [onRequestAnnotations, onAnnotationIconClick]);

  const applySpotlight = useCallback((options: SpotlightApplyOptions) => {
    if (Array.isArray(options)) {
      const items = options.filter(isValidSpotlightConfig);
      if (!items.length) return;

      const { lastSelector } = spotlightStateRef.current;
      if (lastSelector) {
        clearSpotlightUtil(VIEWER_EXPRESS_CONTAINER_ID, lastSelector);
      }
      spotlightStateRef.current = { spotlight: null, lastSelector: null };

      if (batchSpotlightsRef.current.length) {
        clearSpotlightsUtil(
          VIEWER_EXPRESS_CONTAINER_ID,
          batchSpotlightsRef.current.map((item) => item.selector),
        );
      }
      batchSpotlightsRef.current = items;
      applySpotlightsUtil(VIEWER_EXPRESS_CONTAINER_ID, items);
      return;
    }

    if (!isValidSpotlightConfig(options)) return;

    if (batchSpotlightsRef.current.length) {
      clearSpotlightsUtil(
        VIEWER_EXPRESS_CONTAINER_ID,
        batchSpotlightsRef.current.map((item) => item.selector),
      );
      batchSpotlightsRef.current = [];
    }

    const { lastSelector } = spotlightStateRef.current;
    if (lastSelector && lastSelector !== options.selector) {
      clearSpotlightUtil(VIEWER_EXPRESS_CONTAINER_ID, lastSelector);
    }
    spotlightStateRef.current = { spotlight: options, lastSelector: options.selector };
    applySpotlightUtil(VIEWER_EXPRESS_CONTAINER_ID, options);
  }, []);

  const clearSpotlight = useCallback(() => {
    const { lastSelector } = spotlightStateRef.current;
    if (lastSelector) {
      clearSpotlightUtil(VIEWER_EXPRESS_CONTAINER_ID, lastSelector);
    }
    if (batchSpotlightsRef.current.length) {
      clearSpotlightsUtil(
        VIEWER_EXPRESS_CONTAINER_ID,
        batchSpotlightsRef.current.map((item) => item.selector),
      );
    }
    spotlightStateRef.current = { spotlight: null, lastSelector: null };
    batchSpotlightsRef.current = [];
  }, []);

  const addAnnotation = useCallback((annotation: IAnnotation) => {
    addAnnotationModule(annotation);
  }, []);

  const print = useCallback(() => {
    printContainer(hostRef.current);
  }, []);

  /**
   * Exposes imperative methods for directly calling not rendering or not React related functionality.
   *
   */
  useImperativeHandle(
    ref,
    () => ({
      applySpotlight,
      clearSpotlight,
      addAnnotation,
      destroyPageObserver: destroyPageObserver,
      notifyPageByDataId,
      print,
    }),
    [applySpotlight, clearSpotlight, addAnnotation, destroyPageObserver, notifyPageByDataId, print],
  );

  // Before paint, so a reused DOM tree cannot flash the previous chapter's highlights.
  useLayoutEffect(() => {
    if (activePage?.elementId) {
      annotationScreen(activePage.elementId);
    }
  }, [activePage?.elementId]);

  useEffect(() => {
    const key = activePage?.elementId ?? "screen"; //QUESTION: in which situation we have "screen" as key?
    if (prevScreenKeyRef.current !== undefined && prevScreenKeyRef.current !== key) {
      clearSpotlight();
    }
    prevScreenKeyRef.current = key;
  }, [activePage?.elementId, clearSpotlight]);

  // Reset scroll before paint when navigating to a different screen.
  useLayoutEffect(() => {
    if (!containerEl || !activePage?.elementId) {
      return;
    }
    resetScrollRoot(containerEl);
  }, [activePage?.elementId, containerEl]);

  useEffect(() => {
    if (!activePage) return;
    pendingTodoRef.current = { dataId: activePage.dataId, elementId: activePage.elementId };
  }, [activePage?.screenConfig, activePage?.elementId, activePage?.dataId]);

  const onViewerMounted = useCallback(() => {
    if (batchSpotlightsRef.current.length) {
      applySpotlightsUtil(VIEWER_EXPRESS_CONTAINER_ID, batchSpotlightsRef.current);
    }
    const spot = spotlightStateRef.current.spotlight;
    if (spot && isValidSpotlightConfig(spot)) {
      applySpotlightUtil(VIEWER_EXPRESS_CONTAINER_ID, spot);
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const mathRoot = document.getElementById(VIEWER_EXPRESS_CONTAINER_ID);
        renderViewerMathInContainer(mathRoot);
        const todo = pendingTodoRef.current;

        resetScrollRoot(containerEl);
        setupPageObserver();

        if (todo) {
          if (todo.dataId) {
            scrollToElementByDataId(todo.dataId, 20, 100, { behavior: "auto" });
          }
          onComponentReRendered?.(todo.elementId);
        }
      });
    });
  }, [containerEl, onComponentReRendered, setupPageObserver]);

  useEffect(() => {
    if (!activePage?.screenConfig) {
      return;
    }
    onViewerMounted();
  }, [activePage?.screenConfig, onViewerMounted]);

  useEffect(() => {
    if (!containerEl) {
      return;
    }
    return attachDelegatedExternalLinkClick(containerEl, () => ({
      onLinkClick: onLinkClickRef.current,
      deviceType: deviceTypeRef.current,
      renderer: rendererRef.current,
    }));
  }, [containerEl]);

  return (
    <div ref={hostRef} style={hostStyle}>
      <div id={VIEWER_EXPRESS_CONTAINER_ID} ref={(el) => setContainerEl(el)}>
        {containerEl && activePage ? (
          <div className="context-learner">
            <div className="stage-container">
              <div className="stage-box">
                <StageComponent
                  key={activePage.elementId ?? "screen"}
                  env={env}
                  screenConfig={activePage.screenConfig}
                  themeVariant={activeThemeVariant}
                  viewerDevice={viewerDevice}
                  onClickReferenceLink={onClickReferenceLink}
                  onRequestGlossaryData={onRequestGlossaryData}
                  onRequestAuthToken={onRequestAuthToken}
                  onMediaClick={onMediaClick}
                  onMediaPlay={onMediaPlay}
                  onLinkClick={onLinkClick}
                  renderer={renderer}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
});
